import { useState } from 'react';

import { MtxButton } from '@meilleurtaux/mtp-uikit-react';
import { useTranslation } from 'react-i18next';

import VideoTutorial from '@/components/VideoTutorial';
import VideoTutorialRow from '@/components/VideoTutorialRow';
import VIDEO_TUTORIALS_MODAL_MODE from '@/enums/videoTutorialsModalMode';

import type { TutorielVideo, VideoTutorialsModalEntry } from '@/types/videoTutorials';

import styles from '@/components/VideoTutorialsModal/styles.module.css';

interface VideoTutorialsModalContentProps {
  entry: VideoTutorialsModalEntry;
  partnerName: string;
  onPlay?: (tutorial: TutorielVideo) => void;
  onPartnerAccess: () => void;
  isPartnerAccessDisabled?: boolean;
}

const VideoTutorialsModalContent = ({
  entry,
  partnerName,
  onPlay,
  onPartnerAccess,
  isPartnerAccessDisabled = false,
}: VideoTutorialsModalContentProps) => {
  const { t } = useTranslation();
  const isSingle = entry.mode === VIDEO_TUTORIALS_MODAL_MODE.SINGLE;
  const [selectedTutorial, setSelectedTutorial] = useState(isSingle ? entry.tutorial : entry.tutorials[0]);
  const [autoStart, setAutoStart] = useState(isSingle);
  const [hasStarted, setHasStarted] = useState(isSingle);
  const [playerKey, setPlayerKey] = useState(0);

  const getTitle = (tutorial: TutorielVideo) => t(`videoTutorials.tutoTitle.${tutorial.operation}`, { partnerName });

  const handlePlay = (tutorial: TutorielVideo) => {
    setHasStarted(true);
    onPlay?.(tutorial);
  };

  const handleSelect = (tutorial: TutorielVideo) => {
    if (tutorial.videoId === selectedTutorial?.videoId) {
      if (hasStarted) return;
      setPlayerKey((previous) => previous + 1);
    } else {
      setSelectedTutorial(tutorial);
    }
    setAutoStart(true);
  };

  const handlePartnerAccess = () => {
    if (isPartnerAccessDisabled) return;
    onPartnerAccess();
  };

  return (
    <>
      <div className={styles.body}>
        {entry.mode === VIDEO_TUTORIALS_MODAL_MODE.ALL && (
          <ul className={styles.list}>
            {entry.tutorials.map((tutorial) => (
              <li key={tutorial.videoId}>
                <VideoTutorialRow
                  title={getTitle(tutorial)}
                  duree={tutorial.duree}
                  isSelected={tutorial.videoId === selectedTutorial?.videoId}
                  onClick={() => handleSelect(tutorial)}
                />
              </li>
            ))}
          </ul>
        )}
        {selectedTutorial && (
          <div className={styles.player}>
            <VideoTutorial
              key={`${selectedTutorial.videoId}-${playerKey}`}
              tutorial={selectedTutorial}
              title={getTitle(selectedTutorial)}
              autoStart={autoStart}
              onPlay={handlePlay}
            />
          </div>
        )}
      </div>
      <div className={styles.actions}>
        <MtxButton
          variant="primary"
          size="medium"
          fullWidth
          className={styles.cta}
          disabled={isPartnerAccessDisabled}
          onClick={handlePartnerAccess}
        >
          <div slot="start-icon" className={styles.buttonIcon}>
            <i className="totem-icon totem-icon-external-link" aria-hidden="true" />
          </div>
          {t('videoTutorials.partnerAccessCta')}
        </MtxButton>
      </div>
    </>
  );
};

export default VideoTutorialsModalContent;
